import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, Search, Calendar, Tag, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'news', name: 'News' },
  { id: 'tutorials', name: 'Tutorials' },
  { id: 'creator-spotlight', name: 'Creator Spotlight' },
  { id: 'announcements', name: 'Announcements' },
  { id: 'community', name: 'Community' }
];

export function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Fetch posts
  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/blog/posts?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPosts(data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  // Filter posts
  const filteredPosts = posts.filter(post => {
    const matchesSearch = !searchTerm || 
      post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || post.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Delete post
  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/blog/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Post deleted');
        fetchPosts();
      } else {
        toast.error('Failed to delete post');
      }
    } catch (error) {
      toast.error('Error deleting post');
    }
  };

  // Toggle publish status
  const togglePublish = async (post) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/blog/posts/${post.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_published: !post.is_published })
      });
      
      if (res.ok) {
        toast.success(post.is_published ? 'Post unpublished' : 'Post published');
        fetchPosts();
      }
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  if (showEditor) {
    return (
      <BlogEditor 
        post={editingPost}
        onClose={() => {
          setShowEditor(false);
          setEditingPost(null);
        }}
        onSave={() => {
          setShowEditor(false);
          setEditingPost(null);
          fetchPosts();
        }}
      />
    );
  }

  return (
    <div data-testid="admin-blog">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-teko">Blog Management</h2>
          <p className="text-sm text-[var(--text-secondary)]">{posts.length} total posts</p>
        </div>
        <Button 
          onClick={() => setShowEditor(true)}
          className="flex items-center gap-2"
          data-testid="create-post-btn"
        >
          <Plus size={18} />
          New Post
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={18} />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search posts..."
            className="pl-10"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2 bg-[var(--surface)] border border-[var(--border)] rounded-lg"
        >
          <option value="">All Categories</option>
          {CATEGORIES.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      </div>

      {/* Posts Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="text-center py-12 bg-[var(--surface)] border border-[var(--border)] rounded-xl">
          <FileText size={48} className="mx-auto mb-4 text-[var(--text-secondary)]" />
          <p className="text-[var(--text-secondary)]">No blog posts found</p>
          <Button onClick={() => setShowEditor(true)} className="mt-4">
            Create your first post
          </Button>
        </div>
      ) : (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-[var(--background)] border-b border-[var(--border)]">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-[var(--text-secondary)]">Title</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[var(--text-secondary)]">Category</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[var(--text-secondary)]">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[var(--text-secondary)]">Stats</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[var(--text-secondary)]">Date</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-[var(--text-secondary)]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {filteredPosts.map((post) => (
                <tr key={post.id} className="hover:bg-[var(--background)]/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {post.cover_image && (
                        <img 
                          src={post.cover_image} 
                          alt="" 
                          className="w-12 h-8 object-cover rounded"
                        />
                      )}
                      <div>
                        <p className="font-medium line-clamp-1">{post.title}</p>
                        <p className="text-xs text-[var(--text-secondary)] line-clamp-1">{post.excerpt}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-[var(--primary)]/10 text-[var(--primary)] text-xs rounded-full capitalize">
                      {post.category?.replace('-', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      post.is_published 
                        ? 'bg-green-500/10 text-green-500' 
                        : 'bg-yellow-500/10 text-yellow-500'
                    }`}>
                      {post.is_published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                    <div className="flex items-center gap-3">
                      <span>{post.views || 0} views</span>
                      <span>{post.likes || 0} likes</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                    {new Date(post.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => togglePublish(post)}
                        className="p-2 hover:bg-[var(--background)] rounded-lg transition-colors"
                        title={post.is_published ? 'Unpublish' : 'Publish'}
                      >
                        {post.is_published ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button
                        onClick={() => {
                          setEditingPost(post);
                          setShowEditor(true);
                        }}
                        className="p-2 hover:bg-[var(--background)] rounded-lg transition-colors text-blue-500"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="p-2 hover:bg-[var(--background)] rounded-lg transition-colors text-red-500"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Blog Editor Component
function BlogEditor({ post, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: post?.title || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    cover_image: post?.cover_image || '',
    category: post?.category || 'news',
    tags: post?.tags?.join(', ') || '',
    read_time: post?.read_time || 5,
    is_published: post?.is_published ?? false
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      toast.error('Title and content are required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = post 
        ? `${API_URL}/api/admin/blog/posts/${post.id}`
        : `${API_URL}/api/admin/blog/posts`;
      
      const res = await fetch(url, {
        method: post ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
        })
      });

      if (res.ok) {
        toast.success(post ? 'Post updated' : 'Post created');
        onSave();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save post');
      }
    } catch (error) {
      toast.error('Error saving post');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="blog-editor">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-teko">{post ? 'Edit Post' : 'Create New Post'}</h2>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter post title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Excerpt</label>
              <Input
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Brief description for previews"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Cover Image URL</label>
              <Input
                value={formData.cover_image}
                onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                placeholder="https://..."
              />
              {formData.cover_image && (
                <img 
                  src={formData.cover_image} 
                  alt="Cover preview" 
                  className="mt-2 w-full h-32 object-cover rounded-lg border border-[var(--border)]"
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 bg-[var(--surface)] border border-[var(--border)] rounded-lg"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Read Time (min)</label>
                <Input
                  type="number"
                  value={formData.read_time}
                  onChange={(e) => setFormData({ ...formData, read_time: parseInt(e.target.value) || 5 })}
                  min="1"
                  max="60"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Tags (comma-separated)</label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="webcomics, tutorial, tips"
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_published"
                checked={formData.is_published}
                onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="is_published" className="text-sm">Publish immediately</label>
            </div>
          </div>

          {/* Right Column - Content */}
          <div>
            <label className="block text-sm font-medium mb-2">Content * (Markdown supported)</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Write your blog post content here..."
              className="w-full h-[400px] px-4 py-3 bg-[var(--surface)] border border-[var(--border)] rounded-lg resize-none font-mono text-sm"
              required
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-4 pt-4 border-t border-[var(--border)]">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : (post ? 'Update Post' : 'Create Post')}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default AdminBlog;
